import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Iconify from '../components/Iconify';
import textCommandLibraryCategories from '../data/textCommandLibrary';

const allLibraryCommands = textCommandLibraryCategories.flatMap((category) =>
  category.commands.map((command) => ({ ...command, category: category.name }))
);

function normalize(value) {
  return value.trim().toLowerCase();
}

export default function TextCommandLibraryDialog({
  open,
  onClose,
  onEditSelected,
  existingMatches,
  freeSlots,
  isPaid,
}) {
  const [categoryId, setCategoryId] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState([]);
  const existingSet = React.useMemo(() => new Set(existingMatches.map(normalize)), [existingMatches]);

  React.useEffect(() => {
    if (open) {
      setCategoryId('all');
      setQuery('');
      setSelected([]);
    }
  }, [open]);

  const filteredCommands = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return allLibraryCommands.filter((command) => {
      const matchesCategory = categoryId === 'all' || command.category === textCommandLibraryCategories.find((category) => category.id === categoryId)?.name;
      const matchesQuery =
        !normalizedQuery ||
        command.match.toLowerCase().includes(normalizedQuery) ||
        command.description.toLowerCase().includes(normalizedQuery) ||
        command.category.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesQuery;
    });
  }, [categoryId, query]);

  const importableCommands = filteredCommands.filter((command) => !existingSet.has(normalize(command.match)));
  const overFreeLimit = !isPaid && selected.length > freeSlots;

  const toggleCommand = (command) => {
    const key = normalize(command.match);
    if (existingSet.has(key)) {
      return;
    }
    setSelected((previous) =>
      previous.includes(key) ? previous.filter((selectedKey) => selectedKey !== key) : [...previous, key]
    );
  };

  const selectVisible = () => {
    const visibleKeys = importableCommands.map((command) => normalize(command.match));
    const visibleSet = new Set(visibleKeys);
    const hasUnselected = visibleKeys.some((key) => !selected.includes(key));
    if (hasUnselected) {
      setSelected((previous) => Array.from(new Set([...previous, ...visibleKeys])));
    } else {
      setSelected((previous) => previous.filter((key) => !visibleSet.has(key)));
    }
  };

  const selectedCommands = allLibraryCommands.filter((command) => selected.includes(normalize(command.match)));

  const doImport = () => {
    if (selectedCommands.length === 0 || overFreeLimit) {
      return;
    }
    onEditSelected(selectedCommands);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'secondary.lighter',
                color: 'secondary.dark',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Iconify icon="solar:library-bold-duotone" width={22} height={22} />
            </Box>
            <Box>
              <Typography variant="h4">Import command library</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Pick ready-made voice shortcuts, then edit each one before saving.
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {!isPaid && (
            <Alert severity={freeSlots > 0 ? 'info' : 'warning'} sx={{ borderRadius: 2 }}>
              Free accounts can import {freeSlots} more custom {freeSlots === 1 ? 'command' : 'commands'}.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              fullWidth
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search library"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={20} height={20} />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" onClick={selectVisible} disabled={importableCommands.length === 0}>
              {importableCommands.every((command) => selected.includes(normalize(command.match)))
                ? 'Clear visible'
                : 'Select visible'}
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip
              label={`All ${allLibraryCommands.length}`}
              color={categoryId === 'all' ? 'secondary' : 'default'}
              onClick={() => setCategoryId('all')}
            />
            {textCommandLibraryCategories.map((category) => (
              <Chip
                key={category.id}
                label={`${category.name} ${category.commands.length}`}
                color={categoryId === category.id ? 'secondary' : 'default'}
                onClick={() => setCategoryId(category.id)}
              />
            ))}
          </Stack>

          {overFreeLimit && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              You selected {selected.length} commands, but only {freeSlots} custom command slots are available.
            </Alert>
          )}

          <List
            sx={{
              maxHeight: 460,
              overflow: 'auto',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 0,
            }}
          >
            {filteredCommands.map((command) => {
              const key = normalize(command.match);
              const isDuplicate = existingSet.has(key);
              const isSelected = selected.includes(key);

              return (
                <ListItem key={`${command.category}-${command.match}`} disablePadding divider>
                  <ListItemButton disabled={isDuplicate} onClick={() => toggleCommand(command)}>
                    <ListItemIcon>
                      <Checkbox edge="start" checked={isSelected || isDuplicate} disabled={isDuplicate} tabIndex={-1} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Typography variant="subtitle2">Say "{command.match}"</Typography>
                          <Chip label={command.category} size="small" variant="outlined" />
                          {isDuplicate && <Chip label="Already added" size="small" color="info" variant="outlined" />}
                        </Stack>
                      }
                      secondary={command.description}
                      secondaryTypographyProps={{ sx: { whiteSpace: 'pre-line' } }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={doImport} disabled={selectedCommands.length === 0 || overFreeLimit}>
          Import {selectedCommands.length} selected
        </Button>
      </DialogActions>
    </Dialog>
  );
}
